/**
 * MovementTab — Phase 6c.2a per Frontend Spec §21.8.2 (matrix amendment).
 *
 * Phase 6c.2 shipped a stacked-tables-per-class layout that was rejected
 * by the user on 2026-05-14 — the canonical Big4 Coca-Cola / Ford /
 * Turkcell 2025 audited reports use a single matrix table per ROU
 * surface (rows = movement row types, columns = asset classes + TOPLAM).
 * This module is the spec-conformant rewrite of that layout.
 *
 * Composition (in order):
 *   1. ROU Cost            — matrix (Açılış / 7 row types / Kapanış)
 *   2. ROU Acc. Dep.       — matrix (Açılış / 5 row types / Kapanış)
 *   3. Lease Liability     — flat table (10 row types + ST/LT footer)
 *   4. Maturity Analysis   — 6 §B53 buckets + 2 reconciliation cards
 *
 * Audit-paper rules (universal):
 *   • All canonical row types render even when amount is "0" — auditor
 *     expects to see the full IFRS disclosure template.
 *   • Only PRESENT asset classes get a column. Empty classes are hidden.
 *   • TOPLAM column gets accent border + bg + bold text.
 *   • Negative amounts wrap in parentheses + `text-destructive` via
 *     `formatSigned`.
 *   • Opening/Closing rows get the canonical bold + border-top emphasis.
 */

import type { TFunction } from "i18next";
import { Info } from "lucide-react";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatSigned } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  ASSET_CLASS_ORDER,
  MATURITY_BUCKET_ORDER,
  type AssetClass,
  type AssetClassMovementTable,
  type EngagementHistoryEntry,
  type MaturityAnalysis,
  type MovementRowType,
  type MovementTable,
} from "@/types";
import type { AuditMovementSchedule } from "@/features/engagement/audit-movement-schedule";
import type { CalculationResult } from "@/types/result";

// EXCEL-UI-ENHANCE-01 Item 4 (UI) — tie-out tolerance. Mirrors the Excel
// Hareket self-check (movementSheet.ts SELF_CHECK_TOLERANCE) AND the
// Çapraz Kontrol sheet TOLERANCE. Kuruş-level; same value all three
// surfaces use so the auditor sees consistent pass/fail across them.
const TIE_OUT_TOLERANCE = 0.01;

interface OutletContext {
  engagement: EngagementHistoryEntry;
  schedule: AuditMovementSchedule;
}

/**
 * Canonical row order per surface. `OPENING`/`CLOSING` are bookend
 * synthetic keys; everything else maps 1:1 to `MovementRowType`.
 */
type MatrixRowKey = "OPENING" | MovementRowType | "CLOSING";

// SPEC-11 — ROU is non-monetary (IAS 21.23(b)); no FX retranslation.
// LT→ST RECLASSIFICATION is a liability-only presentation split. Both are
// structurally impossible on the ROU surfaces and are therefore dropped.
const ROU_COST_ROWS: MatrixRowKey[] = [
  "OPENING",
  "ADDITIONS",
  "MODIFICATIONS",
  "TERMINATIONS",
  "INDEX_REMEASUREMENT",
  "CLOSING",
];

// SPEC-12 Part B — drop AccDep TERMINATIONS row. The cutoffBucketing AccDep
// loop never adds a `TERMINATIONS` entry (terminations close via
// `DEPRECIATION_REVERSAL`), so the row is structurally always zero. The
// engagement schedule canon and the Bilanço Excel already omit it; this
// brings the Movement UI/Excel templates into line.
const ROU_ACCDEP_ROWS: MatrixRowKey[] = [
  "OPENING",
  "DEPRECIATION",
  "DEPRECIATION_REVERSAL",
  "MODIFICATIONS",
  "CLOSING",
];

const LIABILITY_ROWS: MatrixRowKey[] = [
  "OPENING",
  "ADDITIONS",
  "MODIFICATIONS",
  "INTEREST_ACCRUAL",
  "PAYMENTS",
  "TERMINATIONS",
  "INDEX_REMEASUREMENT",
  "FX_REMEASUREMENT",
  "FX_REVALUATION",
  "RECLASSIFICATION",
  "CLOSING",
];

export function MovementTab() {
  const { t } = useTranslation();
  const { engagement } = useOutletContext<OutletContext>();
  const currency = engagement.functionalCurrency;
  const aggs = engagement.lastResult?.aggregations;
  if (!aggs) {
    return (
      <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
    );
  }

  // EXCEL-UI-ENHANCE-01 Item 5 (UI) — surface the per-contract schedules
  // so the liability PAYMENTS row can split into faiz / anapara.
  // perContract is empty for engagements that haven't been calculated;
  // the split sub-rows omit themselves cleanly when missing.
  const perContract = engagement.lastResult?.perContract ?? [];
  const auditPeriodStart = engagement.auditPeriodStart;
  const auditPeriodEnd = engagement.auditPeriodEnd;
  return (
    <div className="space-y-6">
      <AssetClassMovementMatrix
        title={t("movement.section.rouAssetCost")}
        rowOrder={ROU_COST_ROWS}
        table={aggs.movement.rouAssetCost}
        currency={currency}
        testId="mv-rou-cost"
      />
      <AssetClassMovementMatrix
        title={t("movement.section.rouAccumulatedDepreciation")}
        rowOrder={ROU_ACCDEP_ROWS}
        table={aggs.movement.rouAccumulatedDepreciation}
        currency={currency}
        testId="mv-rou-accdep"
      />
      <LeaseLiabilitySection
        table={aggs.movement.leaseLiability}
        shortTerm={aggs.balanceSheet.leaseLiability.closingShortTerm}
        longTerm={aggs.balanceSheet.leaseLiability.closingLongTerm}
        closing={aggs.balanceSheet.leaseLiability.closing}
        currency={currency}
        perContract={perContract}
        auditPeriodStart={auditPeriodStart}
        auditPeriodEnd={auditPeriodEnd}
      />
      <MaturityAnalysisSection
        analysis={aggs.maturityAnalysis}
        currency={currency}
      />
    </div>
  );
}

// ─── AssetClassMovementMatrix ─────────────────────────────────────────────
//
// One single shadcn `Table` per ROU surface.
//   • Header row: row-label column + present-class columns + TOPLAM
//   • Body: one `<tr>` per canonical row type; cells iterate the
//     class columns then the TOPLAM column.
//   • Reconciliation variance footer: rendered ONLY when MANUAL mode
//     reported a variance on the TOTAL aggregate.

interface AssetClassMovementMatrixProps {
  title: string;
  rowOrder: MatrixRowKey[];
  table: AssetClassMovementTable;
  currency: string;
  testId: string;
}

function AssetClassMovementMatrix({
  title,
  rowOrder,
  table,
  currency,
  testId,
}: AssetClassMovementMatrixProps) {
  const { t } = useTranslation();
  const presentClasses = ASSET_CLASS_ORDER.filter(
    (ac) => table.byAssetClass[ac] !== undefined,
  );

  function cellValue(rowKey: MatrixRowKey, column: AssetClass | "TOTAL"): string {
    const source: MovementTable =
      column === "TOTAL" ? table.total : table.byAssetClass[column]!;
    if (rowKey === "OPENING") return source.openingBalance;
    if (rowKey === "CLOSING") return source.closingBalance;
    return source.rows.find((r) => r.type === rowKey)?.amount ?? "0";
  }

  function rowLabel(rowKey: MatrixRowKey): string {
    if (rowKey === "OPENING") return t("movement.openingBalance");
    if (rowKey === "CLOSING") return t("movement.closingBalance");
    return t(`movement.row.${rowKey}` as never) as string;
  }

  return (
    <Card className="p-6 glow-card" data-testid={`mv-section-${testId}`}>
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="overflow-x-auto w-full">
      <Table className="min-w-max" data-testid={`${testId}-matrix`}>
        <TableHeader>
          <TableRow>
            <TableHead className="w-64" />
            {presentClasses.map((ac) => (
              <TableHead
                key={ac}
                className="text-right whitespace-nowrap"
                data-testid={`${testId}-col-${ac}`}
              >
                {t(`form.assetClass.${ac}` as never) as string}
              </TableHead>
            ))}
            <TableHead
              className="text-right font-bold whitespace-nowrap border-l-2 border-primary bg-accent/20"
              data-testid={`${testId}-col-TOTAL`}
            >
              {t("movement.total")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rowOrder.map((rowKey) => {
            const isOpening = rowKey === "OPENING";
            const isClosing = rowKey === "CLOSING";
            const emphasize = isOpening || isClosing;
            const rowTestId =
              isOpening
                ? `${testId}-row-opening`
                : isClosing
                  ? `${testId}-row-closing`
                  : `${testId}-row-${rowKey}`;
            // EXCEL-UI-ENHANCE-01 Item 6 (UI) — FX-line explanation tooltip.
            // §23 / §28 wording matches the Excel adjacent-cell text exactly
            // (movementSheet.ts fxExplanation) so UI and Excel agree on the
            // standard reference. Only the FX rows get the icon; everything
            // else renders the plain label.
            const fxAdorn = rowInfoTooltip(rowKey, t);
            return (
              <TableRow key={rowKey} data-testid={rowTestId}>
                <TableCell
                  className={cn(
                    "text-sm",
                    emphasize && "font-semibold",
                    isClosing && "border-t-2 border-border",
                  )}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {rowLabel(rowKey)}
                    {fxAdorn}
                  </span>
                </TableCell>
                {presentClasses.map((ac) => (
                  <TableCell
                    key={ac}
                    className={cn(
                      "text-sm text-right font-mono tabular-nums",
                      emphasize && "font-semibold",
                      isClosing && "border-t-2 border-border",
                      isNegative(cellValue(rowKey, ac)) && "text-destructive",
                      // Phase BB5-3 — fade exact-zero cells.
                      isZeroAmount(cellValue(rowKey, ac)) &&
                        "text-muted-foreground/50",
                    )}
                    data-testid={`${testId}-cell-${ac}-${rowKey}`}
                  >
                    {formatSigned(cellValue(rowKey, ac), currency)}
                  </TableCell>
                ))}
                <TableCell
                  className={cn(
                    "text-sm text-right font-mono tabular-nums",
                    "border-l-2 border-primary bg-accent/20",
                    emphasize ? "font-bold" : "font-semibold",
                    isClosing && "border-t-2 border-border",
                    isNegative(cellValue(rowKey, "TOTAL")) &&
                      "text-destructive",
                    // Phase BB5-3 — fade exact-zero totals.
                    isZeroAmount(cellValue(rowKey, "TOTAL")) &&
                      "text-muted-foreground/50",
                  )}
                  data-testid={`${testId}-cell-TOTAL-${rowKey}`}
                >
                  {formatSigned(cellValue(rowKey, "TOTAL"), currency)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>

      {/* Phase 6c.2 / Spec §21.8.2 v1.20 — MANUAL-mode reconciliation
          variance is footer-only (Balance Sheet shows the user-supplied
          opening verbatim). Surfaces below the TOPLAM row, never inside
          a per-class column. */}
      {table.total.reconciliationVariance !== undefined && (
        <p
          className="mt-3 text-sm text-muted-foreground"
          data-testid={`${testId}-reconciliation-variance`}
        >
          <span className="font-medium">
            {t("movement.reconciliationVariance.label")}:
          </span>{" "}
          <span className="font-mono tabular-nums">
            {formatSigned(table.total.reconciliationVariance, currency)}
          </span>
          <span className="ml-2 text-xs italic">
            {t("movement.reconciliationVariance.hint")}
          </span>
        </p>
      )}

      {/* EXCEL-UI-ENHANCE-01 Item 4 (UI) — per-matrix tie-out footer on
          the TOPLAM (total-of-classes) table. The per-class tie-outs
          would clutter the matrix; the TOPLAM is the auditor's primary
          surface and matches the Excel Hareket per-block tie-out
          structurally. Intentional overlap with Çapraz Kontrol per
          user "Seçenek 1". */}
      <TieOutFooter table={table.total} currency={currency} testId={testId} />
    </Card>
  );
}

/**
 * Info-icon tooltip adorning movement-row labels with their IFRS reference.
 *   • FX_REMEASUREMENT / FX_REVALUATION → IAS 21 §23 / §28 (EXCEL-UI-ENHANCE-01 Item 6)
 *   • INDEX_REMEASUREMENT → IFRS 16.42(b)/.43 index remeasurement (SPEC-13 Part B)
 * Returns null on every other row so callers can render unconditionally.
 * `t` is threaded in because this is a module-level helper, not a component —
 * `useTranslation()` can only be called from React components or other hooks.
 * The two call sites already have `t` in scope from their own `useTranslation()`.
 */
function rowInfoTooltip(
  rowKey: MatrixRowKey,
  t: TFunction,
): React.ReactNode {
  let label: string | null = null;
  let testId = "";
  if (rowKey === "FX_REMEASUREMENT") {
    label = t("movement.fxTooltip.remeasurement", {
      defaultValue: "Dönem içi yeniden ölçüm (IAS 21 §23)",
    });
    testId = "mv-fx-remeasurement-info";
  } else if (rowKey === "FX_REVALUATION") {
    label = t("movement.fxTooltip.revaluation", {
      defaultValue: "Dönem sonu değerleme (IAS 21 §28)",
    });
    testId = "mv-fx-revaluation-info";
  } else if (rowKey === "INDEX_REMEASUREMENT") {
    label = t("movement.indexTooltip", {
      defaultValue:
        "Endekse bağlı kira ödemelerinin (ör. TÜFE) yeniden ölçümü. Kira " +
        "yükümlülüğü, değişmeyen iskonto oranıyla yeniden hesaplanır; aynı " +
        "tutar kullanım hakkı varlığına yansıtılır ve kâr/zarara gitmez " +
        "(IFRS 16.42(b), .43). Varlık sıfırın altına inerse fark gelir " +
        "olarak yazılır (IFRS 16.39).",
    });
    testId = "mv-index-remeasurement-info";
  }
  if (label === null) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info
          size={12}
          className="text-muted-foreground cursor-help"
          data-testid={testId}
          aria-label={label}
        />
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">{label}</TooltipContent>
    </Tooltip>
  );
}

function isNegative(amount: string): boolean {
  const n = Number(amount);
  return Number.isFinite(n) && n < 0;
}

// ─── EXCEL-UI-ENHANCE-01 Item 4 (UI) — per-block self-check helpers ────
//
// Mirrors the Excel Hareket Item 4 logic (movementSheet.ts
// addAssetClassMatrixTieOut + addFlatTableTieOut + emitTieOutRow). The
// UI and Excel paths read the same `MovementTable` and apply the same
// arithmetic, so they always agree to the cent on the same dataset.
//
// Sign convention: movement-row `amount` values are SIGNED relative to
// the closing balance (PAYMENTS negative for liability; DEPRECIATION
// positive for accDep). The identity to verify:
//     Closing = Opening + Σ row amounts                  (within ±0.01)

function computeTieOutDiff(table: MovementTable): {
  computedClosing: number;
  reportedClosing: number;
  diff: number;
  pass: boolean;
} {
  const opening = Number(table.openingBalance) || 0;
  const flowSum = table.rows.reduce(
    (acc, r) => acc + (Number(r.amount) || 0),
    0,
  );
  const computedClosing = opening + flowSum;
  const reportedClosing = Number(table.closingBalance) || 0;
  const diff = reportedClosing - computedClosing;
  return {
    computedClosing,
    reportedClosing,
    diff,
    pass: Math.abs(diff) < TIE_OUT_TOLERANCE,
  };
}

/**
 * Compact self-check footer rendered under each movement block.
 * Visually a muted italic row, not a data row — so the auditor sees
 * it as a working-paper sign-off rather than another flow.
 */
function TieOutFooter({
  table,
  currency,
  testId,
}: {
  table: MovementTable;
  currency: string;
  testId: string;
}) {
  const { t } = useTranslation();
  const { computedClosing, reportedClosing, diff, pass } = computeTieOutDiff(
    table,
  );
  return (
    <p
      className={cn(
        "mt-3 text-xs italic flex flex-wrap items-center gap-x-2",
        pass ? "text-muted-foreground" : "text-amber-700 dark:text-amber-300",
      )}
      data-testid={`${testId}-tie-out`}
      data-pass={pass ? "true" : "false"}
    >
      <span className="font-medium">
        {t("movement.tieOut.label", {
          defaultValue: "Kapanış = Açılış + Σ hareketler",
        })}
        :
      </span>
      <span className="font-mono tabular-nums">
        {t("movement.tieOut.computed", { defaultValue: "Hesaplanan" })}{" "}
        {formatSigned(String(computedClosing.toFixed(2)), currency)}
      </span>
      <span aria-hidden="true">·</span>
      <span className="font-mono tabular-nums">
        {t("movement.tieOut.reported", { defaultValue: "Bildirilen" })}{" "}
        {formatSigned(String(reportedClosing.toFixed(2)), currency)}
      </span>
      <span aria-hidden="true">·</span>
      <span className="font-mono tabular-nums">
        {t("movement.tieOut.diff", { defaultValue: "Fark" })}{" "}
        {formatSigned(String(diff.toFixed(2)), currency)}
      </span>
      <span
        className={cn(
          "font-bold not-italic",
          pass ? "text-green-700 dark:text-green-300" : "",
        )}
        data-testid={`${testId}-tie-out-status`}
      >
        {pass ? "✓" : "⚠"}
      </span>
    </p>
  );
}

// ─── EXCEL-UI-ENHANCE-01 Item 5 (UI) — payment interest/principal split ─
//
// Replicates the Excel movementSheet.ts `computePaymentSplit` logic so
// the UI sub-rows agree with the Excel sub-rows to the cent on the same
// engagement. Both functions:
//   • iterate per-contract schedules over the audit window
//     (row.date ∈ [auditPeriodStart, auditPeriodEnd]) — cash anchor,
//     matches backend cutoffBucketing.ts post-MINI_FIX_CO Option C;
//   • use *Functional columns when the contract is FX, native otherwise;
//   • derive functional principal as (paymentFunctional −
//     interestExpenseFunctional) because SchedulePeriod doesn't carry a
//     functional principal column;
//   • negate the totals so the sub-rows mirror the PAYMENTS movement
//     row's signed-negative convention.
//
// This is the same shape as movementSheet.ts but kept in float terms
// (the UI uses Number throughout for display). Movement aggregator
// PAYMENTS is already toFixed(2)-shaped, so the two surfaces agree at
// the kuruş cap. If a future fix needs BigInt-cents precision on the UI
// side it can adopt sumDecimalStrings; for now the UI's display
// arithmetic mirrors the toNumber() convention the rest of this tab
// uses (e.g. computeTieOutDiff above).
function computePaymentSplit(
  perContract: ReadonlyArray<CalculationResult>,
  auditPeriodStart: string,
  auditPeriodEnd: string,
): { interest: number; principal: number } {
  let interestSum = 0;
  let principalSum = 0;
  for (const calc of perContract) {
    const isFx = calc.isFxContract === true;
    for (const p of calc.schedule ?? []) {
      if (p.date < auditPeriodStart || p.date > auditPeriodEnd) continue;
      const intStr = isFx
        ? p.interestExpenseFunctional ?? p.interestExpense
        : p.interestExpense;
      const intN = Number(intStr);
      if (!Number.isFinite(intN)) continue;
      let prinN: number;
      if (isFx) {
        const payN = Number(p.paymentFunctional ?? p.payment);
        if (!Number.isFinite(payN)) continue;
        prinN = payN - intN;
      } else {
        prinN = Number(p.principalRepayment);
        if (!Number.isFinite(prinN)) continue;
      }
      interestSum += intN;
      principalSum += prinN;
    }
  }
  // Negate so the sub-rows mirror the PAYMENTS movement-row sign
  // (PAYMENTS reduces the liability and is signed-negative on the
  // movement aggregation). With this negation, interest + principal ==
  // PAYMENTS row total.
  return { interest: -interestSum, principal: -principalSum };
}

/**
 * Phase BB5-3 — true when the cell value is exactly zero (after parsing).
 * Used to fade the cell to half-opacity muted-foreground so visually-empty
 * rows recede and meaningful values stand out.
 */
function isZeroAmount(amount: string): boolean {
  const n = Number(amount);
  return Number.isFinite(n) && n === 0;
}

// ─── LeaseLiabilitySection ──────────────────────────────────────────────────
//
// Class-agnostic surface: a single Amount column. Every canonical row
// type is rendered (template consistency) — zero amounts still show
// `0,00 CCY`. ST/LT split footer is preserved from Phase 6a.13.

interface LeaseLiabilitySectionProps {
  table: MovementTable;
  shortTerm: string;
  longTerm: string;
  closing: string;
  currency: string;
  // EXCEL-UI-ENHANCE-01 Item 5 (UI) — per-contract schedules and audit
  // bounds for the PAYMENTS interest/principal split. All optional: when
  // missing (engagement not yet calculated, or audit bounds unset), the
  // split sub-rows are omitted cleanly and the info icon on PAYMENTS
  // still surfaces the IAS 7 explanation.
  perContract?: ReadonlyArray<CalculationResult>;
  auditPeriodStart?: string;
  auditPeriodEnd?: string;
}

function LeaseLiabilitySection({
  table,
  shortTerm,
  longTerm,
  closing,
  currency,
  perContract,
  auditPeriodStart,
  auditPeriodEnd,
}: LeaseLiabilitySectionProps) {
  const { t } = useTranslation();

  function valueFor(rowKey: MatrixRowKey): string {
    if (rowKey === "OPENING") return table.openingBalance;
    if (rowKey === "CLOSING") return table.closingBalance;
    return table.rows.find((r) => r.type === rowKey)?.amount ?? "0";
  }

  function labelFor(rowKey: MatrixRowKey): string {
    if (rowKey === "OPENING") return t("movement.openingBalance");
    if (rowKey === "CLOSING") return t("movement.closingBalance");
    return t(`movement.row.${rowKey}` as never) as string;
  }

  // EXCEL-UI-ENHANCE-01 Item 5 (UI) — pre-compute the audit-window
  // faiz / anapara split. Mirrors movementSheet.ts computePaymentSplit
  // semantics (cash-anchor window, *Functional for FX, principal derived
  // as paymentFunctional − interestFunctional for FX). Sub-rows render
  // only when both bounds + at least one schedule are present.
  const paymentSplit =
    perContract !== undefined &&
    perContract.length > 0 &&
    auditPeriodStart !== undefined &&
    auditPeriodEnd !== undefined
      ? computePaymentSplit(perContract, auditPeriodStart, auditPeriodEnd)
      : undefined;

  return (
    <Card className="p-6 glow-card" data-testid="mv-section-liability">
      <h3 className="text-lg font-semibold mb-4">
        {t("movement.section.leaseLiability")}
      </h3>
      <div className="overflow-x-auto w-full">
      <Table className="min-w-max" data-testid="mv-liability-table">
        <TableBody>
          {LIABILITY_ROWS.map((rowKey) => {
            const isOpening = rowKey === "OPENING";
            const isClosing = rowKey === "CLOSING";
            const emphasize = isOpening || isClosing;
            const amount = valueFor(rowKey);
            const rowTestId = isOpening
              ? "mv-liability-row-opening"
              : isClosing
                ? "mv-liability-row-closing"
                : `mv-liability-row-${rowKey}`;
            // EXCEL-UI-ENHANCE-01 Item 6 (UI) — §23/§28 tooltip on FX rows.
            const fxAdorn = rowInfoTooltip(rowKey, t);
            // EXCEL-UI-ENHANCE-01 Item 5 (UI) — IAS 7 info icon on the
            // PAYMENTS row label.
            const paymentsAdorn =
              rowKey === "PAYMENTS" ? <PaymentsInfoTooltip /> : null;
            return (
              <>
                <TableRow key={rowKey} data-testid={rowTestId}>
                  <TableCell
                    className={cn(
                      "text-sm",
                      emphasize && "font-semibold",
                      isClosing && "border-t-2 border-border",
                    )}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {labelFor(rowKey)}
                      {paymentsAdorn}
                      {fxAdorn}
                    </span>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-sm text-right font-mono tabular-nums",
                      emphasize && "font-semibold",
                      isClosing && "border-t-2 border-border",
                      isNegative(amount) && "text-destructive",
                      // Phase BB5-3 — fade exact-zero amounts.
                      isZeroAmount(amount) && "text-muted-foreground/50",
                    )}
                  >
                    {formatSigned(amount, currency)}
                  </TableCell>
                </TableRow>
                {/* EXCEL-UI-ENHANCE-01 Item 5 (UI) — split sub-rows */}
                {rowKey === "PAYMENTS" && paymentSplit !== undefined && (
                  <>
                    <TableRow data-testid="mv-liability-row-PAYMENTS-faiz">
                      <TableCell className="text-sm italic text-muted-foreground pl-8">
                        {t("movement.payments.interest", {
                          defaultValue: "Faiz (IAS 7)",
                        })}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-sm text-right font-mono tabular-nums italic text-muted-foreground",
                          paymentSplit.interest < 0 && "text-destructive/70",
                        )}
                      >
                        {formatSigned(paymentSplit.interest.toFixed(2), currency)}
                      </TableCell>
                    </TableRow>
                    <TableRow data-testid="mv-liability-row-PAYMENTS-anapara">
                      <TableCell className="text-sm italic text-muted-foreground pl-8">
                        {t("movement.payments.principal", {
                          defaultValue: "Anapara (IAS 7)",
                        })}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-sm text-right font-mono tabular-nums italic text-muted-foreground",
                          paymentSplit.principal < 0 &&
                            "text-destructive/70",
                        )}
                      >
                        {formatSigned(
                          paymentSplit.principal.toFixed(2),
                          currency,
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>
      </div>

      {/* ST/LT split footer — preserved across phases. */}
      <div className="mt-4 pt-4 border-t flex flex-wrap justify-end gap-6 text-sm">
        <div data-testid="mv-liability-st">
          <span className="text-muted-foreground">
            {t("movement.shortTermClosing")}:
          </span>{" "}
          <span className="font-mono tabular-nums font-medium">
            {formatSigned(shortTerm, currency)}
          </span>
        </div>
        <div data-testid="mv-liability-lt">
          <span className="text-muted-foreground">
            {t("movement.longTermClosing")}:
          </span>{" "}
          <span className="font-mono tabular-nums font-medium">
            {formatSigned(longTerm, currency)}
          </span>
        </div>
        <div data-testid="mv-liability-footer-closing">
          <span className="font-semibold">
            {t("movement.closingBalance")}:
          </span>{" "}
          <span className="font-mono tabular-nums font-bold">
            {formatSigned(closing, currency)}
          </span>
        </div>
      </div>

      {/* EXCEL-UI-ENHANCE-01 Item 4 (UI) — liability per-block tie-out. */}
      <TieOutFooter
        table={table}
        currency={currency}
        testId="mv-section-liability"
      />
    </Card>
  );
}

/**
 * EXCEL-UI-ENHANCE-01 Item 5 (UI) — IAS 7 cash-flow classification
 * tooltip on the PAYMENTS row label. Surfaces the rationale for the
 * faiz/anapara split sub-rows that follow when per-contract schedules
 * are available.
 */
function PaymentsInfoTooltip() {
  const { t } = useTranslation();
  const label = t("movement.payments.infoTooltip", {
    defaultValue:
      "IAS 7 nakit akış sınıflandırması için faiz/anapara ayrımı.",
  });
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Info
          size={12}
          className="text-muted-foreground cursor-help"
          data-testid="mv-payments-info"
          aria-label={label}
        />
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">{label}</TooltipContent>
    </Tooltip>
  );
}

// ─── MaturityAnalysisSection ───────────────────────────────────────────────

interface MaturityAnalysisSectionProps {
  analysis: MaturityAnalysis;
  currency: string;
}

function MaturityAnalysisSection({
  analysis,
  currency,
}: MaturityAnalysisSectionProps) {
  const { t } = useTranslation();
  const byBucket = new Map(analysis.ranges.map((r) => [r.bucket, r.amount]));
  return (
    <Card className="p-6 glow-card" data-testid="mv-section-maturity">
      <h3 className="text-lg font-semibold mb-1">
        {t("maturity.section.title")}
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        {t("maturity.section.subtitle")}
      </p>

      <div className="overflow-x-auto w-full">
      <Table className="min-w-max">
        <TableBody>
          {MATURITY_BUCKET_ORDER.map((bucket) => (
            <TableRow
              key={bucket}
              data-testid={`mv-maturity-bucket-${bucket}`}
            >
              <TableCell className="text-sm">
                {t(`maturity.bucket.${bucket}` as never) as string}
              </TableCell>
              <TableCell className="text-sm text-right font-mono tabular-nums">
                {formatSigned(byBucket.get(bucket) ?? "0", currency)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            data-testid="mv-maturity-total"
            className="border-t-2 font-bold"
          >
            <TableCell className="text-sm font-bold border-t-2 border-border">
              {t("maturity.total")}
            </TableCell>
            <TableCell className="text-sm text-right font-mono tabular-nums font-bold border-t-2 border-border">
              {formatSigned(analysis.total, currency)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <Card
          className="p-4 bg-muted/30 glow-card"
          data-testid="mv-maturity-discounted-total"
        >
          <p className="text-xs text-muted-foreground">
            {t("maturity.discountedTotal.title")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("maturity.discountedTotal.subtitle")}
          </p>
          <p className="mt-1 text-lg font-semibold font-mono tabular-nums">
            {formatSigned(analysis.discountedTotal, currency)}
          </p>
        </Card>
        <Card
          className="p-4 bg-muted/30 glow-card"
          data-testid="mv-maturity-future-interest"
        >
          <p className="text-xs text-muted-foreground">
            {t("maturity.futureInterest.title")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("maturity.futureInterest.subtitle")}
          </p>
          <p className="mt-1 text-lg font-semibold font-mono tabular-nums">
            {formatSigned(analysis.futureInterest, currency)}
          </p>
        </Card>
      </div>
    </Card>
  );
}

export type { AssetClass };
